/* ============================================================
   Knobs.co — Algolia headless search integration (ported from Knobs.co).

   Strategy: hybrid SSR + JS override.
     - Shopify renders knobs-search-with-filters.liquid with native
       search.results on first paint (SEO-safe + no-JS fallback).
     - On JS load, this script REPLACES the rendered sidebar +
       grid with results from Algolia. Same DOM (.knobs-srch__*),
       same CSS, same finish swatches.
     - On Algolia error, leaves the SSR content in place (graceful
       degradation back to Shopify native).

   Coordination with knobs-live-filter.js:
     - This script disables knobs-live-filter.js (sets
       window.tkAlgoliaActive=true; live-filter checks for that).
     - This script owns ALL filter/sort/page event handling on /search.
     - knobs-live-filter.js continues to handle /collections/* (which is
       still Shopify native + Search & Discovery).

   API credentials:
     - Application ID: YQLK7JGQ6E (public)
     - Search-Only API Key: client-exposed by design
     - Index: shopify_products (record count TBD — depends on imported catalog)
     - Replica: shopify_products_recently_ordered_count_desc (popularity)
   ============================================================ */

(function () {
  'use strict';

  // Only run on /search page
  if (!/^\/search(\?|$|\/)/.test(location.pathname + location.search)) return;

  // ─── 2026-05-19 (migration day) — Algolia takeover DISABLED ───
  // Algolia is not configured for knobs.co post-migration: this script
  // makes ZERO Algolia API calls here (index not connected), yet it was
  // unconditionally setting window.tkAlgoliaActive=true below, which
  // permanently disabled knobs-live-filter.js on /search — so EVERY
  // search-page filter was dead (the SSR sidebar had no handler at all;
  // Kayla report). Hand /search back to native Shopify search +
  // knobs-live-filter.js (the same mechanism that works on /collections).
  // REVERSIBLE: delete this guard block to restore the Algolia takeover
  // once the Algolia index/credentials are live for this store.
  return;

  // Tell knobs-live-filter.js to stand down on this page
  window.tkAlgoliaActive = true;

  const APP_ID = 'YQLK7JGQ6E';
  const SEARCH_KEY = '11defc1b66b77421e9de1469f21f250d';
  // Typesense = primary engine; Algolia kept as fail-open fallback (old names below).
  const TS_HOST = '8dcxftm9ab6hl0s4p-1.a2.typesense.net';
  const TS_SEARCH_KEY = 'uaK8IihBH8YEqaZPgyLVG1GsBNsOlcpJ'; // search-only scope
  // body_html_safe is EXCLUDED from query_by: it made group_by queries with 4+
  // tokens pathologically slow (22s -> 47ms, verified 2026-07-09) AND let
  // description text hijack category intent (backplates ranking for 'knobs and
  // pulls'). Descriptions are still stored/displayed, just not searched.
  // named_tags_names dropped 2026-07-27 (zero hits on real queries, ~5% cost).
  // upc/barcode STAY — populated since the lighting expansion (verified live).
  const TS_QUERY_BY = 'product_type,title,sku,upc,barcode,vendor,tags';
  const INDEX_PRIMARY = 'products';
  const INDEX_POPULAR = 'products/sort/recently_ordered_count:desc,boost_score:desc';
  const INDEX_PRICE_UP = 'products/sort/price:asc';
  const INDEX_PRICE_DOWN = 'products/sort/price:desc';
  const ALG_INDEX_MAP = {
    'products': 'shopify_products',
    'products/sort/recently_ordered_count:desc,boost_score:desc': 'shopify_products_recently_ordered_count_desc',
    'products/sort/price:asc': 'shopify_products_price_asc',
    'products/sort/price:desc': 'shopify_products_price_desc',
  };
  let _tsAdapter = null;
  function tsClient() {
    if (!_tsAdapter && window.TypesenseInstantSearchAdapter) {
      _tsAdapter = new window.TypesenseInstantSearchAdapter({
        server: {
          apiKey: TS_SEARCH_KEY,
          nodes: [{ host: TS_HOST, port: 443, protocol: 'https' }],
          connectionTimeoutSeconds: 4,
          cacheSearchResultsForSeconds: 60,
        },
        // Facet VALUES contain colons ('Style: Modern') — without this list the
      // adapter mis-parses 'tags:Style: Modern' and matches loosely (verified:
      // 74 results where exact = 64). Listing the facet fields pins the split.
      facetableFieldsWithSpecialCharacters: ['tags', 'vendor', 'product_type', 'meta.details.finish', 'finish', 'collections', 'collection_ids', 'price_range', 'option_names', 'family_key'],
      additionalSearchParameters: {
          query_by: TS_QUERY_BY,
          sort_by: '_text_match(buckets:6):desc,boost_score:desc,recently_ordered_count:desc',
          group_by: 'family_key',
          group_limit: 1,
          drop_tokens_threshold: 1,
          prioritize_exact_match: true,
        },
      });
    }
    return _tsAdapter && _tsAdapter.searchClient;
  }
  const HITS_PER_PAGE = 24;

  /* ───────── Finish-name → hex color map (mirror of knobs-finish-swatch.liquid) ───────── */
  const FINISH_HEX = {
    'matte black': '#0d0d0d', 'flat black': '#1d1d1f', 'tuscan bronze': '#7d5a30',
    'honey bronze': '#b48342', 'oil rubbed bronze': '#2a1a0e', 'umbrio': '#1f1814',
    'german bronze': '#5d3530', 'antique bronze': '#5e4a36', 'polished nickel': '#c8c2b0',
    'brushed satin nickel': '#bdb9ad', 'satin nickel': '#bdb9ad', 'pewter': '#8e8e88',
    'pewter antique': '#6f6c64', 'antique pewter': '#6f6c64', 'brushed pewter': '#9a9892',
    'brushed satin brass': '#b89a5a', 'brushed brass': '#b89a5a', 'satin brass': '#c4a868',
    'polished brass': '#d4af37', 'unlacquered brass': '#b89a5a', 'brushed gold': '#cdb380',
    'gold': '#d4af37', 'rose gold': '#b76e79', 'champagne bronze': '#a8895c',
    'polished chrome': '#d8dde0', 'chrome': '#d8dde0', 'brushed chrome': '#b8b8b8',
    'matte chrome': '#a8a8a8', 'polished copper': '#b87333', 'antique copper': '#7e4427',
    'brushed copper': '#9a5e34', 'antique english': '#4a3a2a', 'flat white': '#f5f5f0',
    'matte white': '#f5f5f0', 'white': '#ffffff', 'gunmetal': '#2a3439', 'graphite': '#383838',
    'silver': '#bcbcb6', 'mahogany bronze': '#4a2c1d', 'cocoa bronze': '#52382a',
    'verde antique': '#5b6e58', 'patina': '#7a8a6b', 'crystal': '#e8e8f0', 'glass': '#e8e8f0',
    'porcelain': '#faf8f3', 'mother of pearl': '#f4ecd8', 'horn': '#d4b896',
    'leather': '#8b6b3d', 'wood': '#a0826d', 'ash gray': '#bcb6a8', 'ash grey': '#bcb6a8',
    'sable': '#352620', 'brushed bronze': '#7e6a4f', 'aged bronze': '#5e5132',
    'brushed nickel': '#9c9a93', 'slate': '#525a62', 'venetian bronze': '#6e4326',
    'brushed stainless steel': '#a9a9a8', 'silicon bronze light': '#9c8470',
    'light bronze': '#a48060', 'medium bronze': '#7d5a3a', 'patina rouge': '#84443a',
    'patina black': '#2c2c28', 'polished stainless steel': '#c0c0bc',
    'old english copper': '#6e4426', 'cast iron': '#2a2826', 'dark antique brass': '#4a3a20',
    'aluminum': '#b8b8b6', 'pewter light': '#a8a59f', 'brass antique': '#8a7644',
    'coal black': '#1c1c1a', 'iron': '#3a3936', 'black nickel': '#2a2826', 'rust': '#9c5a3a',
    // Master finish-class names (17 top-level categories) added 2026-05-11 — needed for facet pills + mega menu
    'brass': '#b89a5a', 'copper': '#b87333', 'nickel': '#c8c2b0', 'steel': '#8a8c8e',
    'gray': '#888888', 'grey': '#888888', 'brown': '#8b5a2b', 'clear': '#e8e8f0',
    'colored': 'conic-gradient(from 0deg,#e74c3c,#f1c40f,#2ecc71,#3498db,#9b59b6,#e74c3c)',
    // Broad-catalog finishes added 2026-05-11 (top-count options.finish without swatches)
    'bronze': '#7a5a3a', 'burnished brass': '#b8923f', 'museum gold plate': '#d6b65b',
    'antique nickel': '#9c958a', 'burnished silver plate': '#c4c0b4',
    'florentine gold plate': '#ad8a3c', 'matte sterling silver': '#b4b2ad',
    'satin gold': '#c4a558', 'french bronze': '#5a3a24', 'artisian brass': '#a88848',
    'artisian copper': '#9a6138', 'artisian pewter': '#8a8784', 'burnished pewter': '#88857e',
    'vintage pewter': '#797772', 'chocolate bronze': '#3d2616', 'golden champagne': '#c8a772',
    'barcelona': '#826142', 'polished antique': '#b8a06a', 'antique english matte': '#4a3a2a',
    'black bronze': '#2a1f15', 'stainless': '#b8b8b6', 'stainless steel': '#c0c0bc',
    'satin stainless': '#a8a8a4', 'black': '#0a0a0a', 'antique': '#7a6038', 'lustre': '#c4c0b6',
    'mink': '#7a6e60', 'natural': '#a87f50', 'oil rubbed': '#2a1a0e', 'wrought': '#2a2826',
    'antique brass': '#8a7644', 'champagne': '#d6c08e', 'matte nickel': '#b0aea4',
    'matte satin nickel': '#b0aea4', 'dull satin nickel': '#a8a59c', 'black matte': '#0d0d0d',
    'brushed black': '#1f1f1f', 'brushed black nickel': '#2a2826', 'dark bronze': '#3a2618',
    'rust bronze': '#8e4b30', 'antique silver': '#a8a59c', 'sterling nickel': '#c0bcb0',
    'aged pewter': '#6a685f', 'gilded bronze': '#8a6a3e', 'weathered nickel': '#8a8780',
    'weathered brass': '#8a7440', 'matte gold': '#c4a85a', 'matte brass': '#ad8a40',
    'matte satin brass': '#b89a5a',
    // Two-tone combos
    'champagne & matte black': 'linear-gradient(90deg,#d6c08e 50%,#1d1d1f 50%)',
    'matte nickel & matte black': 'linear-gradient(90deg,#b0aea4 50%,#1d1d1f 50%)',
    'polished nickel / black bronze': 'linear-gradient(90deg,#c8c2b0 50%,#2a1f15 50%)',
    'antique bronze & copper': 'linear-gradient(90deg,#5e4a36 50%,#9a5e34 50%)',
    'clear / oil-rubbed bronze': 'linear-gradient(90deg,#e8e8f0 50%,#2a1a0e 50%)',
    'clear / golden champagne': 'linear-gradient(90deg,#e8e8f0 50%,#c8a772 50%)',
    'clear / champagne bronze': 'linear-gradient(90deg,#e8e8f0 50%,#a8895c 50%)',
    'polished nickel / golden champagne': 'linear-gradient(90deg,#c8c2b0 50%,#c8a772 50%)',
    'polished nickel / gunmetal': 'linear-gradient(90deg,#c8c2b0 50%,#2a3439 50%)',
    'polished nickel / stainless steel': 'linear-gradient(90deg,#c8c2b0 50%,#c0c0bc 50%)',
    'satin nickel / polished chrome': 'linear-gradient(90deg,#bdb9ad 50%,#d8dde0 50%)',
    'brushed gold / black chrome': 'linear-gradient(90deg,#cdb380 50%,#2a2826 50%)',
    'flat black and honey bronze': 'linear-gradient(90deg,#1d1d1f 50%,#b48342 50%)',
    'honey bronze and flat black': 'linear-gradient(90deg,#b48342 50%,#1d1d1f 50%)',
    'flat black and pewter antique': 'linear-gradient(90deg,#1d1d1f 50%,#6f6c64 50%)',
    'pewter antique and flat black': 'linear-gradient(90deg,#6f6c64 50%,#1d1d1f 50%)'
  };

  function finishHex(name) {
    return FINISH_HEX[String(name || '').toLowerCase().trim()] || '';
  }

  /* ───────── DOM helpers ───────── */
  function el(html) {
    const t = document.createElement('template');
    t.innerHTML = html.trim();
    return t.content.firstChild;
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
  function fmtMoney(num) {
    if (num == null || isNaN(num)) return '';
    return '$' + Number(num).toFixed(2);
  }

  /* ───────── URL state ↔ search params ───────── */
  function readState() {
    const p = new URLSearchParams(location.search);
    const state = {
      q: p.get('q') || '',
      type: p.get('type') || 'product',
      sort: p.get('sort_by') || 'relevance',
      page: Math.max(0, (parseInt(p.get('page') || '1', 10) - 1)),
      priceMin: p.get('filter.v.price.gte') || '',
      priceMax: p.get('filter.v.price.lte') || '',
      tags: p.getAll('filter.p.tag'),
      productTypes: p.getAll('filter.p.product_type'),
      vendors: p.getAll('filter.p.vendor'),
      drillCenters: p.getAll('filter.p.drill_centers')
    };
    return state;
  }

  function writeUrl(state) {
    const p = new URLSearchParams();
    if (state.q) p.set('q', state.q);
    if (state.type) p.set('type', state.type);
    if (state.sort && state.sort !== 'relevance') p.set('sort_by', state.sort);
    if (state.page > 0) p.set('page', String(state.page + 1));
    if (state.priceMin) p.set('filter.v.price.gte', state.priceMin);
    if (state.priceMax) p.set('filter.v.price.lte', state.priceMax);
    state.tags.forEach(t => p.append('filter.p.tag', t));
    state.productTypes.forEach(t => p.append('filter.p.product_type', t));
    state.vendors.forEach(t => p.append('filter.p.vendor', t));
    state.drillCenters.forEach(t => p.append('filter.p.drill_centers', t));
    const qs = p.toString();
    const url = location.pathname + (qs ? '?' + qs : '');
    history.pushState({}, '', url);
  }

  /* ───────── Algolia query builder ───────── */
  function buildAlgoliaParams(state) {
    // facetFilters: outer = AND, inner = OR
    const facetFilters = [];
    function group(arr, prefix) {
      if (arr.length) facetFilters.push(arr.map(v => `${prefix}:${v}`));
    }
    group(state.tags, 'tags');
    group(state.productTypes, 'product_type');
    group(state.vendors, 'vendor');
    // (options.drill_centers facet retired with the Typesense port — the
    // `options` object was always empty in the index, so it never matched;
    // Typesense errors on unknown filter fields rather than ignoring them.)

    const numericFilters = [];
    if (state.priceMin) numericFilters.push(`price>=${state.priceMin}`);
    if (state.priceMax) numericFilters.push(`price<=${state.priceMax}`);

    const params = {
      query: state.q,
      hitsPerPage: HITS_PER_PAGE,
      page: state.page,
      facets: ['tags', 'product_type', 'vendor', 'price'],
      maxValuesPerFacet: 100,
      clickAnalytics: true,
      attributesToRetrieve: [
        'objectID', 'id', 'title', 'handle', 'vendor', 'product_type',
        'price', 'compare_at_price', 'sku', 'image', 'product_image',
        'tags', 'inventory_available'
      ],
      attributesToHighlight: ['title', 'tags']
    };
    if (facetFilters.length) params.facetFilters = facetFilters;
    if (numericFilters.length) params.numericFilters = numericFilters;

    // Sort handling — sort variants ride on the adapter's "/sort/" index-name
    // convention (Algolia replica equivalents exist in ALG_INDEX_MAP for fallback).
    let indexName = INDEX_PRIMARY;
    if (state.sort === 'price-ascending') {
      indexName = INDEX_PRICE_UP;
    } else if (state.sort === 'price-descending') {
      indexName = INDEX_PRICE_DOWN;
    } else if (state.sort === 'popular') {
      indexName = INDEX_POPULAR;
    }
    return { indexName, params };
  }

  /* ───────── Search API call: Typesense via adapter, fail-open to Algolia ───────── */
  let currentAbort = null;
  let searchSeq = 0; // adapter has no AbortController; drop stale responses by sequence
  async function searchAlgolia(state) {
    const { indexName, params } = buildAlgoliaParams(state);
    const mySeq = ++searchSeq;
    const client = tsClient();
    if (client) {
      try {
        const data = await client.search([{ indexName, params }]);
        if (mySeq !== searchSeq) throw Object.assign(new Error('stale'), { name: 'AbortError' });
        return data.results[0];
      } catch (e) {
        if (e && e.name === 'AbortError') throw e;
        console.warn('[knobs-srch] typesense failed, falling back to algolia', e);
      }
    }
    // Fallback: Algolia direct (original transport, old index names)
    if (currentAbort) currentAbort.abort();
    currentAbort = new AbortController();
    const algIndex = ALG_INDEX_MAP[indexName] || indexName;
    const url = `https://${APP_ID}-dsn.algolia.net/1/indexes/${encodeURIComponent(algIndex)}/query`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': APP_ID,
        'X-Algolia-API-Key': SEARCH_KEY,
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: JSON.stringify({ params: new URLSearchParams(flattenParams(params)).toString() }),
      signal: currentAbort.signal
    });
    if (!res.ok) throw new Error('Algolia search failed: ' + res.status);
    return await res.json();
  }

  function flattenParams(p) {
    const out = {};
    for (const [k, v] of Object.entries(p)) {
      if (v == null) continue;
      out[k] = (typeof v === 'object') ? JSON.stringify(v) : String(v);
    }
    return out;
  }

  /* ───────── Tag display helpers ───────── */
  // Group an array of tag values by their "<Group>:" prefix.
  // E.g. ["Style: Modern", "Finish-Class: Bronze", "primary-finish"]
  // becomes { "Style": ["Modern"], "Finish-Class": ["Bronze"] } — bare tags ignored.
  function groupTagsByPrefix(values) {
    const groups = {};
    // Tag prefixes to suppress from the sidebar.
    //   - primary-finish: internal flag, not a user-facing facet
    //   - country of origin (both casings — Catalin import has inconsistent casing
    //     "Country of Origin" vs "Country Of Origin"; both should be skipped until
    //     the import normalizes to a single form)
    //   - price-range: duplicates the price slider rendered above
    //   - product-flag: surfaces "New Arrival" / "Best Seller" — handled via
    //     dedicated badges on the product card, not a filter
    const SKIP_PREFIXES = /^(primary-finish|country of origin|country\s+of\s+origin|price-range|product-flag)$/i;
    values.forEach(({ value, count }) => {
      const m = /^([^:]+):\s*(.+)$/.exec(value);
      if (!m) return;
      const grp = m[1].trim();
      if (SKIP_PREFIXES.test(grp)) return;
      if (!groups[grp]) groups[grp] = [];
      groups[grp].push({ value, label: m[2].trim(), count });
    });
    return groups;
  }

  // Reverse helper: given a tag-like full value "Style: Modern", strip prefix to "Modern"
  function stripPrefix(s) {
    const m = /^[^:]+:\s*(.+)$/.exec(s);
    return m ? m[1].trim() : s;
  }

  /* ───────── Render: sidebar facets ───────── */

  // Display label for a tag-prefix group. Strips hyphens for general case,
  // overrides a few groups with cleaner customer-facing names.
  function prettyGroupLabel(grpName) {
    const renames = {
      'Finish-Class': 'Finish',
      'Drill-Centers-Range': 'Drill Centers Range'
    };
    if (renames[grpName]) return renames[grpName];
    return grpName.replace(/-/g, ' ');
  }

  function renderSidebar(response, state) {
    const facets = response.facets || {};
    const facetStats = response.facets_stats || {};

    const tagFacets = facets.tags || {};
    const productTypeFacets = facets.product_type || {};
    const vendorFacets = facets.vendor || {};
    const drillCenterFacets = facets['options.drill_centers'] || {};

    const tagValues = Object.entries(tagFacets).map(([value, count]) => ({ value, count }));
    const tagGroups = groupTagsByPrefix(tagValues);

    // Preferred order. Tags not in the list are appended alphabetically.
    // Drill-Centers-Range sits right before the dedicated Drill Centers
    // (specific sizes) facet that's rendered separately below.
    const groupOrder = ['Style', 'Finish-Class', 'Drill-Centers-Range', 'Collection'];
    const orderedTagKeys = [
      ...groupOrder.filter(k => tagGroups[k]),
      ...Object.keys(tagGroups).filter(k => !groupOrder.includes(k)).sort()
    ];

    let html = `<form action="/search" method="get" class="knobs-srch__filter-form" id="knobs-srch-filter-form" data-live-enabled="true">`;
    html += `<input type="hidden" name="q" value="${escapeAttr(state.q)}">`;
    html += `<input type="hidden" name="type" value="product">`;

    // PRICE — slider
    const priceStats = facetStats.price || {};
    const rmax = Math.ceil(priceStats.max || 1000);
    const rmin = 0;
    const cmin = state.priceMin ? Number(state.priceMin) : rmin;
    const cmax = state.priceMax ? Number(state.priceMax) : rmax;
    html += renderPriceFacet(rmin, rmax, cmin, cmax);

    // BRAND (vendor) — Knobs.co is multi-vendor, brand is the top filtering axis
    // Render BEFORE tag groups so it sits at the top, just under Price.
    const vValues = Object.entries(vendorFacets).map(([value, count]) => ({
      paramValue: value, displayLabel: value, count, active: state.vendors.indexOf(value) !== -1
    })).sort((a, b) => b.count - a.count);
    if (vValues.length > 1 || vValues.some(v => v.active)) {
      html += renderListFacet({ label: 'Brand', paramName: 'filter.p.vendor', values: vValues, isFinishLike: false });
    }

    // Tag-based groups (Style / Finish-Class / Collection / etc)
    orderedTagKeys.forEach(grpName => {
      const values = tagGroups[grpName].sort((a, b) => b.count - a.count);
      const isFinish = /finish|color|colour/i.test(grpName);
      html += renderListFacet({
        label: prettyGroupLabel(grpName),
        paramName: 'filter.p.tag',
        values: values.map(v => ({
          paramValue: v.value, // full "Style: Modern"
          displayLabel: v.label, // "Modern"
          count: v.count,
          active: state.tags.indexOf(v.value) !== -1
        })),
        isFinishLike: isFinish
      });

      // Right after the Drill-Centers-Range group, render specific
      // Drill Centers sizes (options.drill_centers) — sourced from the
      // product option facet, not the tag prefix.
      if (grpName === 'Drill-Centers-Range') {
        const dcValues = Object.entries(drillCenterFacets).map(([value, count]) => ({
          paramValue: value, displayLabel: value, count, active: state.drillCenters.indexOf(value) !== -1
        })).sort((a, b) => b.count - a.count);
        if (dcValues.length > 1 || dcValues.some(v => v.active)) {
          html += renderListFacet({ label: 'Drill Centers', paramName: 'filter.p.drill_centers', values: dcValues, isFinishLike: false });
        }
      }
    });

    // If Drill-Centers-Range tag group wasn't rendered (e.g. no products with
    // that tag in this result set), still render the specific Drill Centers
    // facet if any specific sizes are present.
    if (!tagGroups['Drill-Centers-Range']) {
      const dcValues = Object.entries(drillCenterFacets).map(([value, count]) => ({
        paramValue: value, displayLabel: value, count, active: state.drillCenters.indexOf(value) !== -1
      })).sort((a, b) => b.count - a.count);
      if (dcValues.length > 1 || dcValues.some(v => v.active)) {
        html += renderListFacet({ label: 'Drill Centers', paramName: 'filter.p.drill_centers', values: dcValues, isFinishLike: false });
      }
    }

    // product_type
    const ptValues = Object.entries(productTypeFacets).map(([value, count]) => ({
      paramValue: value,
      displayLabel: stripDisplayPrefix(value),
      count,
      active: state.productTypes.indexOf(value) !== -1
    })).sort((a, b) => b.count - a.count);
    if (ptValues.length > 1 || ptValues.some(v => v.active)) {
      html += renderListFacet({ label: 'Type', paramName: 'filter.p.product_type', values: ptValues, isFinishLike: false });
    }

    html += `<button type="submit" class="knobs-btn knobs-btn--gold knobs-srch__filter-apply">Apply Filters</button>`;
    html += `</form>`;
    return html;
  }

  function stripDisplayPrefix(label) {
    if (!label) return '';
    if (label.indexOf('|') !== -1) return label.split('|').pop().trim();
    if (label.indexOf(' - ') !== -1) return label.split(' - ').pop().trim();
    return label;
  }

  function renderListFacet({ label, paramName, values, isFinishLike }) {
    const overflowThreshold = 6;
    const overflowSize = Math.max(0, values.length - overflowThreshold);

    let body = '';
    values.forEach(v => {
      const swatch = isFinishLike ? renderSwatch(v.displayLabel, 18, 'knobs-srch__filter-swatch') : '';
      const cls = [
        'knobs-srch__filter-item',
        isFinishLike ? 'knobs-srch__filter-item--swatch' : '',
        v.count === 0 && !v.active ? 'is-disabled' : '',
        v.active ? 'is-active' : ''
      ].filter(Boolean).join(' ');
      body += `<label class="${cls}">`;
      body += `<input type="checkbox" name="${escapeAttr(paramName)}" value="${escapeAttr(v.paramValue)}"${v.active ? ' checked' : ''}${v.count === 0 && !v.active ? ' disabled' : ''}>`;
      body += swatch;
      body += `<span class="knobs-srch__filter-text">${escapeHtml(v.displayLabel)}</span>`;
      body += `<span class="knobs-srch__filter-count">(${v.count})</span>`;
      body += `</label>`;
    });
    if (overflowSize > 0) {
      body += `<button type="button" class="knobs-srch__filter-more" data-more-count="${overflowSize}" data-more-label="See ${overflowSize} more" data-less-label="See less">See ${overflowSize} more</button>`;
    }
    return `<details class="knobs-srch__filter-group" open><summary class="knobs-srch__filter-label"><span>${escapeHtml(label)}</span><span class="knobs-srch__filter-chev" aria-hidden="true">+</span></summary><div class="knobs-srch__filter-body">${body}</div></details>`;
  }

  function renderSwatch(name, size, extra) {
    const hex = finishHex(name);
    const styleHex = hex ? ` background: ${hex};` : '';
    const unknownCls = hex ? '' : ' knobs-swatch--unknown';
    const initial = hex ? '' : `<span class="knobs-swatch__initial">${escapeHtml((name[0] || '').toUpperCase())}</span>`;
    return `<span class="knobs-swatch ${extra || ''}${unknownCls}" title="${escapeAttr(name)}" style="--tk-swatch-size: ${size || 18}px;${styleHex}" data-finish="${escapeAttr(String(name).toLowerCase())}">${initial}</span>`;
  }

  function renderPriceFacet(rmin, rmax, cmin, cmax) {
    return `
      <details class="knobs-srch__filter-group" open>
        <summary class="knobs-srch__filter-label"><span>Price</span><span class="knobs-srch__filter-chev" aria-hidden="true">+</span></summary>
        <div class="knobs-srch__filter-body">
          <div class="knobs-srch__price-slider" data-min="${rmin}" data-max="${rmax}" data-min-name="filter.v.price.gte" data-max-name="filter.v.price.lte">
            <div class="knobs-srch__price-display">
              <span class="knobs-srch__price-display-low">$${cmin}</span>
              <span class="knobs-srch__price-display-high">$${cmax}</span>
            </div>
            <div class="knobs-srch__price-track-wrap">
              <div class="knobs-srch__price-track"></div>
              <div class="knobs-srch__price-fill"></div>
              <input type="range" class="knobs-srch__price-thumb knobs-srch__price-thumb--low" min="${rmin}" max="${rmax}" value="${cmin}" step="1" aria-label="Minimum price">
              <input type="range" class="knobs-srch__price-thumb knobs-srch__price-thumb--high" min="${rmin}" max="${rmax}" value="${cmax}" step="1" aria-label="Maximum price">
            </div>
            <p class="knobs-srch__price-range-note">Range: $${rmin} – $${rmax}</p>
            <input type="hidden" name="filter.v.price.gte" value="${cmin === rmin ? '' : cmin}" data-price-min-input>
            <input type="hidden" name="filter.v.price.lte" value="${cmax === rmax ? '' : cmax}" data-price-max-input>
          </div>
        </div>
      </details>`;
  }

  /* ───────── Render: main (toolbar + chips + grid + pagination) ───────── */
  function renderMain(response, state) {
    let html = renderToolbar(response, state);
    html += renderChips(state);
    if (response.nbHits === 0) {
      html += renderEmpty(state);
    } else {
      html += renderGrid(response.hits, response.queryID, (state.page || 0) * HITS_PER_PAGE);
      html += renderPagination(response.page, response.nbPages);
    }
    return html;
  }

  function renderToolbar(response, state) {
    const total = response.nbHits || 0;
    const sortOptions = [
      { value: 'relevance', label: 'Relevance' },
      { value: 'price-ascending', label: 'Price: Low to High' },
      { value: 'price-descending', label: 'Price: High to Low' },
      { value: 'popular', label: 'Most Popular' }
    ];
    const sortHtml = sortOptions.map(o =>
      `<option value="${o.value}"${o.value === state.sort ? ' selected' : ''}>${o.label}</option>`
    ).join('');
    return `
      <div class="knobs-srch__toolbar">
        <p class="knobs-srch__count">Showing <strong>${total}</strong> result${total !== 1 ? 's' : ''} for &ldquo;${escapeHtml(state.q)}&rdquo;</p>
        <div class="knobs-srch__sort">
          <label for="knobs-srch-sort">Sort:</label>
          <select id="knobs-srch-sort" name="sort_by" class="knobs-srch__sort-select">${sortHtml}</select>
        </div>
      </div>`;
  }

  function renderChips(state) {
    const chips = [];
    state.tags.forEach(v => {
      const m = /^([^:]+):\s*(.+)$/.exec(v);
      const grp = m ? m[1].trim() : 'Tag';
      const label = m ? m[2].trim() : v;
      const niceGroup = grp === 'Finish-Class' ? 'Finish' : grp;
      chips.push({ kind: 'tag', value: v, label: `${niceGroup}: ${label}` });
    });
    state.productTypes.forEach(v => {
      chips.push({ kind: 'productType', value: v, label: `Type: ${stripDisplayPrefix(v)}` });
    });
    state.vendors.forEach(v => {
      chips.push({ kind: 'vendor', value: v, label: `Brand: ${v}` });
    });
    if (state.priceMin) {
      chips.push({ kind: 'priceMin', value: state.priceMin, label: `Min $${state.priceMin}` });
    }
    if (state.priceMax) {
      chips.push({ kind: 'priceMax', value: state.priceMax, label: `Max $${state.priceMax}` });
    }
    if (!chips.length) return '';
    let html = `<div class="knobs-srch__chips">`;
    chips.forEach(c => {
      html += `<a href="#" class="knobs-srch__chip" data-kind="${escapeAttr(c.kind)}" data-value="${escapeAttr(c.value)}"><span>${escapeHtml(c.label)}</span><span class="knobs-srch__chip-x" aria-hidden="true">×</span></a>`;
    });
    html += `<a href="#" class="knobs-srch__chip-clear" data-clear-all="1">Clear all</a>`;
    html += `</div>`;
    return html;
  }

  function renderEmpty(state) {
    return `
      <div class="knobs-srch__empty">
        <h2 class="knobs-srch__empty-h2">No results found for &ldquo;${escapeHtml(state.q)}&rdquo;</h2>
        <p class="knobs-srch__empty-sub">Try a different search term, or browse by category below.</p>
        <div class="knobs-srch__empty-btns">
          <a href="/collections/all" class="knobs-btn knobs-btn--gold">Browse All Products</a>
          <a href="/pages/contact" class="knobs-btn knobs-btn--ghost-border">Ask for Help</a>
        </div>
      </div>`;
  }

  function renderGrid(hits, queryID, baseOffset) {
    baseOffset = baseOffset || 0;
    // Partition: products with an image first, then products without.
    // Without-image products render as placeholder cards which look unfinished,
    // so push them to the bottom of each page. Matches the SSR Liquid behavior
    // in sections/knobs-search-with-filters.liquid.
    const hasImg = (h) => !!(h.image || h.product_image);
    const withImg = hits.filter(hasImg);
    const withoutImg = hits.filter(h => !hasImg(h));
    const ordered = withImg.concat(withoutImg);
    const cards = ordered.map((h, i) => renderCard(h, queryID, baseOffset + i + 1)).join('');
    return `<div class="knobs-srch__grid">${cards}</div>`;
  }

  function renderCard(hit, queryID, position) {
    // Variant-level index: append ?variant=<id> so a variant SKU hit deep-links
    // to its own variant instead of the product's default variant. Guarded to
    // numeric objectIDs (Shopify variant ids); anything else falls back cleanly.
    const variantQS = /^\d+$/.test(String(hit.objectID)) ? `?variant=${encodeURIComponent(hit.objectID)}` : '';
    const url = `/products/${escapeAttr(hit.handle || '')}${variantQS}`;
    const img = hit.image || hit.product_image || '';
    const onSale = hit.compare_at_price && hit.compare_at_price > hit.price;
    const pct = onSale
      ? Math.round((hit.compare_at_price - hit.price) * 100 / hit.compare_at_price)
      : 0;
    const sale = onSale
      ? `<span class="knobs-pcard__sale-badge">Save ${pct}%</span>`
      : '';
    const compareAt = onSale
      ? `<span class="knobs-pcard__price-was">${fmtMoney(hit.compare_at_price)}</span>`
      : '';
    const sku = hit.sku
      ? `<p class="knobs-pcard__sku">SKU: ${escapeHtml(hit.sku)}</p>`
      : '';
    const imgHtml = img
      ? `<img src="${escapeAttr(img)}" alt="${escapeAttr(hit.title || '')}" class="knobs-pcard__img" loading="lazy">`
      : `<div class="knobs-pcard__img knobs-pcard__img--placeholder"></div>`;
    const insightsAttrs = queryID
      ? ` data-object-id="${escapeAttr(String(hit.objectID))}" data-query-id="${escapeAttr(queryID)}" data-position="${position || 1}"`
      : '';
    return `
      <div class="knobs-pcard">
        <a class="knobs-pcard__link" href="${url}"${insightsAttrs}>
          <div class="knobs-pcard__media">
            ${imgHtml}
            ${sale}
          </div>
          <div class="knobs-pcard__info">
            ${hit.vendor ? `<p class="knobs-pcard__collection">${escapeHtml(hit.vendor)}</p>` : ''}
            <p class="knobs-pcard__name">${escapeHtml(hit.title || '')}</p>
            ${sku}
            <div class="knobs-pcard__bottom">
              <div class="knobs-pcard__price-wrap">
                <span class="knobs-pcard__price">${fmtMoney(hit.price)}</span>
                ${compareAt}
              </div>
            </div>
          </div>
        </a>
      </div>`;
  }

  function renderPagination(page, nbPages) {
    if (nbPages <= 1) return '';
    let html = `<nav class="knobs-srch__pagination" role="navigation">`;
    if (page > 0) {
      html += `<a href="#" class="knobs-srch__page-btn" data-page="${page - 1}">&larr; Previous</a>`;
    }
    // Compact page list: 1 ... (page-1) page (page+1) ... last
    const pages = compactPageList(page, nbPages);
    pages.forEach(item => {
      if (item === '...') {
        html += `<span class="knobs-srch__page-btn">…</span>`;
      } else if (item === page) {
        html += `<span class="knobs-srch__page-btn knobs-srch__page-btn--active">${item + 1}</span>`;
      } else {
        html += `<a href="#" class="knobs-srch__page-btn" data-page="${item}">${item + 1}</a>`;
      }
    });
    if (page < nbPages - 1) {
      html += `<a href="#" class="knobs-srch__page-btn" data-page="${page + 1}">Next &rarr;</a>`;
    }
    html += `</nav>`;
    return html;
  }

  function compactPageList(current, total) {
    const out = new Set([0, total - 1, current, current - 1, current + 1]);
    const sorted = [...out].filter(n => n >= 0 && n < total).sort((a, b) => a - b);
    const compact = [];
    for (let i = 0; i < sorted.length; i++) {
      compact.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) compact.push('...');
    }
    return compact;
  }

  /* ───────── Mount points ───────── */
  function getSidebarEl() { return document.querySelector('.knobs-srch__sidebar'); }
  function getMainEl() { return document.querySelector('.knobs-srch__main'); }

  function applyResponse(response, state) {
    const sidebar = getSidebarEl();
    const main = getMainEl();
    if (!sidebar || !main) return;
    sidebar.innerHTML = renderSidebar(response, state);
    main.innerHTML = renderMain(response, state);
    initPriceSlider();
    initFilterMoreButtons();
  }

  /* ───────── Init dynamic widgets ───────── */
  function initPriceSlider() {
    document.querySelectorAll('.knobs-srch__price-slider').forEach(slider => {
      if (slider.dataset.tkBound === 'true') return;
      slider.dataset.tkBound = 'true';
      const min = parseFloat(slider.dataset.min);
      const max = parseFloat(slider.dataset.max);
      const lowThumb = slider.querySelector('.knobs-srch__price-thumb--low');
      const highThumb = slider.querySelector('.knobs-srch__price-thumb--high');
      const fill = slider.querySelector('.knobs-srch__price-fill');
      const lowDisplay = slider.querySelector('.knobs-srch__price-display-low');
      const highDisplay = slider.querySelector('.knobs-srch__price-display-high');
      const lowInput = slider.querySelector('[data-price-min-input]');
      const highInput = slider.querySelector('[data-price-max-input]');
      if (!lowThumb || !highThumb) return;
      function update(active) {
        let lo = parseFloat(lowThumb.value);
        let hi = parseFloat(highThumb.value);
        if (lo >= hi) {
          if (active === 'low') { lo = hi - 1; lowThumb.value = lo; }
          else { hi = lo + 1; highThumb.value = hi; }
        }
        const range = max - min;
        const lowPct = range ? ((lo - min) / range) * 100 : 0;
        const highPct = range ? ((hi - min) / range) * 100 : 100;
        if (fill) { fill.style.left = lowPct + '%'; fill.style.right = (100 - highPct) + '%'; }
        if (lowDisplay) lowDisplay.textContent = '$' + lo;
        if (highDisplay) highDisplay.textContent = '$' + hi;
        if (lowInput) lowInput.value = lo === min ? '' : Math.round(lo);
        if (highInput) highInput.value = hi === max ? '' : Math.round(hi);
      }
      lowThumb.addEventListener('input', () => update('low'));
      highThumb.addEventListener('input', () => update('high'));
      update();
    });
  }

  function initFilterMoreButtons() {
    document.querySelectorAll('.knobs-srch__filter-more').forEach(btn => {
      if (btn.dataset.tkBound === 'true') return;
      btn.dataset.tkBound = 'true';
      btn.addEventListener('click', e => {
        e.preventDefault();
        const body = btn.closest('.knobs-srch__filter-body');
        if (!body) return;
        const expanded = body.classList.toggle('is-expanded');
        btn.textContent = expanded ? btn.dataset.lessLabel : btn.dataset.moreLabel;
      });
    });
  }

  /* ───────── Event handlers (delegation, survives DOM replace) ───────── */
  document.addEventListener('change', e => {
    if (e.target.closest('#knobs-srch-filter-form')) {
      const newState = stateFromForm();
      run(newState);
      return;
    }
    if (e.target.id === 'knobs-srch-sort') {
      const s = readState();
      s.sort = e.target.value;
      s.page = 0;
      run(s);
      return;
    }
  });

  document.addEventListener('click', e => {
    // Algolia Insights: result-card click → click event + remember queryID
    // so a later add-to-cart can be attributed to this search (conversion).
    const cardLink = e.target.closest('.knobs-pcard__link[data-object-id]');
    if (cardLink && window.KnobsInsights) {
      window.KnobsInsights.recordClick({
        objectID: cardLink.getAttribute('data-object-id'),
        queryID: cardLink.getAttribute('data-query-id'),
        position: cardLink.getAttribute('data-position'),
        eventName: 'PDP Click from Search'
      });
      // do not return — let the navigation proceed normally
    }
    const chip = e.target.closest('.knobs-srch__chip, .knobs-srch__chip-clear');
    if (chip) {
      e.preventDefault();
      const s = readState();
      if (chip.dataset.clearAll) {
        s.tags = []; s.productTypes = []; s.vendors = [];
        s.priceMin = ''; s.priceMax = '';
        s.page = 0;
      } else {
        const kind = chip.dataset.kind;
        const value = chip.dataset.value;
        if (kind === 'tag') s.tags = s.tags.filter(t => t !== value);
        else if (kind === 'productType') s.productTypes = s.productTypes.filter(t => t !== value);
        else if (kind === 'vendor') s.vendors = s.vendors.filter(t => t !== value);
        else if (kind === 'priceMin') s.priceMin = '';
        else if (kind === 'priceMax') s.priceMax = '';
        s.page = 0;
      }
      run(s);
      return;
    }
    const pageBtn = e.target.closest('a.knobs-srch__page-btn[data-page]');
    if (pageBtn) {
      e.preventDefault();
      const s = readState();
      s.page = parseInt(pageBtn.dataset.page, 10) || 0;
      run(s).then(() => window.scrollTo({ top: 0, behavior: 'smooth' }));
      return;
    }
  });

  window.addEventListener('popstate', () => {
    run(readState(), { skipUrlWrite: true });
  });

  function stateFromForm() {
    const form = document.getElementById('knobs-srch-filter-form');
    const sortSel = document.getElementById('knobs-srch-sort');
    const fd = new FormData(form);
    const state = {
      q: fd.get('q') || readState().q,
      type: 'product',
      sort: sortSel ? sortSel.value : 'relevance',
      page: 0,
      priceMin: fd.get('filter.v.price.gte') || '',
      priceMax: fd.get('filter.v.price.lte') || '',
      tags: fd.getAll('filter.p.tag'),
      productTypes: fd.getAll('filter.p.product_type'),
      vendors: fd.getAll('filter.p.vendor')
    };
    return state;
  }

  /* ───────── Main runner ───────── */
  let inFlight = 0;
  async function run(state, opts = {}) {
    const main = getMainEl();
    if (main) main.classList.add('is-loading');
    const myId = ++inFlight;
    try {
      const response = await searchAlgolia(state);
      if (myId !== inFlight) return; // superseded
      applyResponse(response, state);
      if (!opts.skipUrlWrite) writeUrl(state);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('[knobs-algolia-search]', e);
      // Leave SSR content in place on error — this is the graceful fallback.
    } finally {
      const m = getMainEl();
      if (m) m.classList.remove('is-loading');
      document.body.classList.remove('knobs-algolia-loading');
    }
  }

  /* ───────── Boot ───────── */
  function boot() {
    const initial = readState();
    if (!initial.q) return; // no search yet — leave the pre-search hero alone
    document.body.classList.add('knobs-algolia-loading');
    run(initial, { skipUrlWrite: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
